import { useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { Button, CircularProgress } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import IosShareIcon from "@mui/icons-material/IosShare";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import Drawer from "../../../components/ui/Drawer";
import GroupReportDocument from "./GroupReportDocument";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import { buildReportModel, reportBadgeUrls, reportFileName } from "../../../lib/groupReport";
import { loadBadgeImages } from "../../../lib/pdfBadges";
import { ACTIVITY_LABEL_KEYS } from "../../goals/goalRules";
import { pdfBlobToPngBlob, pngFileName } from "../../../lib/pdfToPng";

/** Blob'u tarayıcıya indirtir. */
function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function canShareFile(file) {
  return Boolean(navigator.canShare && navigator.canShare({ files: [file] }));
}

/**
 * Grup ilerleme raporu: önizleme ve indirme AYNI blob üzerinden çalışır,
 * böylece ekranda görülen belge ile indirilen dosya birebir aynıdır.
 */
export default function GroupReportDrawer({ open, group, userStats, range, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  const [pngBusy, setPngBusy] = useState(false);

  const model = useMemo(
    () =>
      buildReportModel({
        group,
        userStats,
        range,
        activityName: (activity) => t(ACTIVITY_LABEL_KEYS[activity] || activity),
      }),
    [group, userStats, range, t]
  );
  const fileName = reportFileName(group?.groupName);

  const labels = useMemo(
    () => ({
      appName: t("app_name"),
      title: t("report_title"),
      rangeLabel: t("manage_stats_range_label"),
      printedAt: t("report_printed_at"),
      activeMembers: t("manage_stats_summary_active_members"),
      activeDays: t("manage_stats_summary_active_days"),
      avgCompletion: t("manage_stats_summary_avg_completion"),
      goal: t("report_col_goal"),
      value: t("report_col_value"),
      completion: t("report_col_completion"),
      member: t("manage_stats_table_member"),
      activeDaysShort: t("report_col_active_days"),
      perfectDays: t("report_col_perfect_days"),
      total: t("report_col_total"),
      noData: t("manage_stats_no_data"),
    }),
    [t]
  );

  // Drawer açılınca belgeyi bir kez üret; kapanınca blob'u bırak.
  useEffect(() => {
    if (!open) {
      setBlob(null);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    setError(false);
    setBlob(null);

    // Rozetler önce PNG'ye çevrilir; çevrilemeyenler belgede atlanır.
    loadBadgeImages(reportBadgeUrls(model))
      .catch(() => ({}))
      .then((badges) => pdf(<GroupReportDocument model={model} labels={labels} badges={badges} />).toBlob())
      .then((result) => {
        if (!cancelled) setBlob(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [open, model, labels]);

  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  // PNG, önizlemedeki PDF'in ta kendisinden üretiliyor; sayfalar alt alta.
  const handlePng = async () => {
    if (!blob) return;
    setPngBusy(true);
    try {
      const png = await pdfBlobToPngBlob(blob);
      downloadBlob(png, pngFileName(fileName));
    } catch {
      toast.error(t("report_png_error"));
    } finally {
      setPngBusy(false);
    }
  };

  const handleShare = async () => {
    if (!blob) return;
    const file = new File([blob], fileName, { type: "application/pdf" });
    if (!canShareFile(file)) {
      toast.info(t("report_share_unsupported"));
      return;
    }
    try {
      await navigator.share({ files: [file], title: group?.groupName });
    } catch (err) {
      // Kullanıcı paylaşımı iptal ettiyse hata göstermiyoruz.
      if (err?.name !== "AbortError") toast.error(t("report_share_error"));
    }
  };

  const shareable = Boolean(
    blob && navigator.canShare && canShareFile(new File([blob], fileName, { type: "application/pdf" }))
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="lg"
      title={t("report_title")}
      subtitle={`${group?.groupName || ""} · ${model.rangeLabel}`}
      titleIcon={<PictureAsPdfIcon />}
      footer={
        <div className="flex items-center gap-2">
          <span className="flex-1 text-xs text-terakkiNeutral truncate dark:text-white/70">{fileName}</span>
          {shareable && (
            <Button variant="outlined" startIcon={<IosShareIcon />} onClick={handleShare}>
              {t("report_share")}
            </Button>
          )}
          <LoadingButton
            variant="outlined"
            startIcon={<ImageOutlinedIcon />}
            loading={pngBusy}
            disabled={!blob}
            onClick={handlePng}
          >
            {t("report_download_png")}
          </LoadingButton>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!blob}
            onClick={() => downloadBlob(blob, fileName)}
          >
            {t("report_download")}
          </Button>
        </div>
      }
    >
      <div className="h-full min-h-[520px] bg-terakkiSurfaceAlt dark:bg-white/[0.05]">
        {error ? (
          <div className="h-full flex items-center justify-center p-6 text-center">
            <p className="text-sm text-terakkiError">{t("report_error")}</p>
          </div>
        ) : url ? (
          <iframe src={url} title={t("report_title")} className="w-full h-full min-h-[520px] border-0" />
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-3 p-6">
            <CircularProgress size={28} />
            <p className="text-sm text-terakkiNeutral dark:text-white/70">{t("report_building")}</p>
          </div>
        )}
      </div>
    </Drawer>
  );
}
